import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  ActivityDonutItem,
  DashboardTimeFilter,
  DashboardTrend,
  UserGrowthChartPoint,
} from "../types/dashboard-ui.types";
import {
  buildActivityDonutItems,
  buildUserGrowthChartPoints,
  getDashboardTrend,
} from "../utils/dashboardViewModels";
import { useUserActivityData } from "./useUserActivityData";
import { useUserGrowthData } from "./useUserGrowthData";

export interface DashboardAnalytics {
  timeFilter: DashboardTimeFilter;
  setTimeFilter: (value: DashboardTimeFilter) => void;
  trend: DashboardTrend;
  growthChartPoints: UserGrowthChartPoint[];
  growthLoading: boolean;
  growthRefreshing: boolean;
  growthError: string | null;
  retryGrowth: () => Promise<void>;
  activityDonutItems: ActivityDonutItem[];
  activityLoading: boolean;
  activityRefreshing: boolean;
  activityError: string | null;
  retryActivity: () => Promise<void>;
}

export const useDashboardAnalytics = (): DashboardAnalytics => {
  const { t } = useTranslation();
  const [timeFilter, setTimeFilter] = useState<DashboardTimeFilter>("this_week");

  const growth = useUserGrowthData();
  const activity = useUserActivityData();

  const { refetch: refetchGrowth } = growth;
  const { refetch: refetchActivity } = activity;

  const growthChartPoints = useMemo(
    () => buildUserGrowthChartPoints(growth.data, timeFilter),
    [growth.data, timeFilter],
  );
  const trend = useMemo(() => getDashboardTrend(growth.data), [growth.data]);
  const activityDonutItems = useMemo(
    () =>
      buildActivityDonutItems(activity.data, {
        activeThisWeek: t("dashboard.charts.legend.active_this_week"),
        activeToday: t("dashboard.charts.legend.active_today"),
        inactive: t("dashboard.charts.legend.inactive"),
      }),
    [activity.data, t],
  );

  const retryGrowth = useCallback(async () => {
    await refetchGrowth();
  }, [refetchGrowth]);
  const retryActivity = useCallback(async () => {
    await refetchActivity();
  }, [refetchActivity]);

  return {
    timeFilter,
    setTimeFilter,
    trend,
    growthChartPoints,
    growthLoading: growth.isLoading,
    growthRefreshing: growth.isRefreshing,
    growthError: growth.error,
    retryGrowth,
    activityDonutItems,
    activityLoading: activity.isLoading,
    activityRefreshing: activity.isRefreshing,
    activityError: activity.error,
    retryActivity,
  };
};
